import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { DetailFormSkeleton } from "./index";
import ListPageCardShell from "./ListPageCardShell";
import TeamPageShell from "./TeamPageShell";
import ProductListSkeleton from "../../pages/masterData/products/components/ProductListSkeleton";
import ProductTypeListSkeleton from "../../pages/masterData/productType/components/ProductTypeListSkeleton";
import ProductCategoryListSkeleton from "../../pages/masterData/productCategory/components/ProductCategoryListSkeleton";
import ServiceTypeListSkeleton from "../../pages/masterData/serviceType/components/ServiceTypeListSkeleton";
import ManufacturerListSkeleton from "../../pages/masterData/manufacturer/components/ManufacturerListSkeleton";
import VendorListSkeleton from "../../pages/masterData/vendor/components/VendorListSkeleton";
import WorkOrderStageListSkeleton from "../../pages/masterData/workorderStage/components/WorkOrderStageListSkeleton";
import RequestedProductsListSkeleton from "../../pages/requestedProducts/components/RequestedProductsListSkeleton";
import CompanyListSkeleton from "../../pages/company/components/CompanyListSkeleton";
import PartnersListSkeleton from "../../pages/partners/components/PartnersListSkeleton";
import PlansListSkeleton from "../../pages/plans/components/PlansListSkeleton";
import AiAddonsListSkeleton from "../../pages/ai-addons/components/AiAddonsListSkeleton";
import TeamPageSkeleton from "../../pages/team/components/TeamPageSkeleton";

const PRODUCT_FILTER_FIELDS = [
  { label: "Product Category", placeholder: "Search category..." },
  { label: "Product Type", placeholder: "Search type..." },
  { label: "Manufacturer", placeholder: "Search manufacturer..." },
];

export function useRouteSuspenseFallback(): ReactNode {
  const { pathname } = useLocation();

  if (/^\/products\/[^/]+$/.test(pathname)) {
    return <DetailFormSkeleton />;
  }
  if (/^\/producttype\/[^/]+$/.test(pathname)) {
    return <DetailFormSkeleton />;
  }
  if (/^\/productcategory\/[^/]+$/.test(pathname)) {
    return <DetailFormSkeleton />;
  }

  if (pathname.startsWith("/products")) {
    return (
      <ListPageCardShell
        searchPlaceholder="Search products..."
        addButtonLabel="Add Product"
        filterFields={PRODUCT_FILTER_FIELDS}
      >
        <ProductListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/producttype")) {
    return (
      <ListPageCardShell searchPlaceholder="Search by Name" addButtonLabel="Add Product Type">
        <ProductTypeListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/productcategory")) {
    return (
      <ListPageCardShell searchPlaceholder="Search by Name" addButtonLabel="Add Product Category">
        <ProductCategoryListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/servicetype")) {
    return (
      <ListPageCardShell searchPlaceholder="Search by Name" addButtonLabel="Add Service Type">
        <ServiceTypeListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/manufacturer")) {
    return (
      <ListPageCardShell searchPlaceholder="Search manufacturers..." addButtonLabel="Add Manufacturer">
        <ManufacturerListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/vendor")) {
    return (
      <ListPageCardShell searchPlaceholder="Search vendors..." addButtonLabel="Add Vendor">
        <VendorListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/workorderstage")) {
    return (
      <ListPageCardShell searchPlaceholder="Search by Name" addButtonLabel="Add Workorder Stage">
        <WorkOrderStageListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/requested-products")) {
    return (
      <ListPageCardShell searchPlaceholder="Search requested products...">
        <RequestedProductsListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/company")) {
    return (
      <ListPageCardShell searchPlaceholder="Search companies..." addButtonLabel="Create Company">
        <CompanyListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/partners")) {
    return (
      <ListPageCardShell searchPlaceholder="Search partners..." addButtonLabel="Create Partners">
        <PartnersListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/plans")) {
    return (
      <ListPageCardShell searchPlaceholder="Search by Name" addButtonLabel="Add Plan">
        <PlansListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/ai-addons")) {
    return (
      <ListPageCardShell searchPlaceholder="Search AI Addons..." addButtonLabel="Create AI Addon">
        <AiAddonsListSkeleton />
      </ListPageCardShell>
    );
  }
  if (pathname.startsWith("/team")) {
    return (
      <TeamPageShell>
        <TeamPageSkeleton count={6} />
      </TeamPageShell>
    );
  }

  return null;
}
